import { autoUpdate, flip, offset, shift, size, useDismiss, useFloating, useInteractions, useTransitionStyles } from '@floating-ui/react';
import React, { ReactNode } from 'react';
import useMap from '../../../app/hooks/use-map';
import useMapInteraction from '../../../hooks/use-map-interaction';

export function MapContextMenu(props: { open: boolean; children: ReactNode; position: { x: number; y: number }; onClose?: () => void }) {
    const mapInteraction = useMapInteraction();
    const map = useMap();

    const virtualElement = React.useMemo(
        () => ({
            getBoundingClientRect: () => {
                return {
                    x: props.position.x,
                    y: props.position.y,
                    top: props.position.y,
                    bottom: props.position.y,
                    left: props.position.x,
                    right: props.position.x,
                    width: 0,
                    height: 0
                };
            }
        }),
        [props.position]
    );

    const { refs, floatingStyles, context } = useFloating({
        placement: 'bottom-start',
        open: props.open,
        whileElementsMounted: autoUpdate,
        onOpenChange(open, event) {
            if (!open) {
                props.onClose?.();
                if (event instanceof MouseEvent) {
                    mapInteraction?.dismissContextMenu(event.view ?? undefined);
                } else {
                    mapInteraction?.dismissContextMenu();
                }
            }
        },

        middleware: [
            offset({ crossAxis: 0, mainAxis: 10 }),
            flip({ mainAxis: true, crossAxis: true }),
            shift({ mainAxis: true, crossAxis: true }),
            size({
                apply: (p) => {
                    if (p.availableHeight < p.elements.floating.scrollHeight) {
                        p.elements.floating.style.maxHeight = Math.max(0, p.availableHeight) + 'px';
                        p.elements.floating.style.overflow = 'auto';
                    } else {
                        p.elements.floating.style.maxHeight = p.availableHeight + 'px';
                        p.elements.floating.style.overflow = 'unset';
                    }
                }
            })
        ]
    });

    const { isMounted, styles: transitionStyles } = useTransitionStyles(context);
    refs.setReference(virtualElement);

    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
    const referenceProps = getReferenceProps();

    // TODO: use context an spread those props into app root element
    React.useEffect(() => {
        for (const key in referenceProps) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            document.documentElement[key] = referenceProps[key];
        }
        return () => {
            for (const key in referenceProps) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                document.documentElement[key] = undefined;
            }
        };
    }, [referenceProps]);

    // Map move as a click away effect
    React.useEffect(() => {
        if (!map) {
            return;
        }

        return map.on('movestart', (e) => {
            // Ignore events from follow me feature
            if ('fromFollow' in e) {
                return;
            }
            props.onClose?.();
            mapInteraction?.dismissContextMenu();
        }).unsubscribe;
    }, [map, mapInteraction]);

    if (!props.open || !isMounted) {
        return null;
    }

    return (
        <div className="z-2 flight-details-scrollbars" ref={refs.setFloating} {...getFloatingProps()} style={{ ...floatingStyles }}>
            <div className="k-card p-0 z-2" style={transitionStyles}>
                {props.children}
            </div>
        </div>
    );
}
